import type { RecipeFormData } from '@/types/recipe'

const baseRecipe = {
  image_url: '',
  rating: null,
  household_id: undefined,
  is_public: true,
} satisfies Partial<RecipeFormData>

export const dutchClassics: RecipeFormData[] = [
  {
    ...baseRecipe,
    title: 'Stamppot Boerenkool met Rookworst',
    description: 'Een klassieke Nederlandse stamppot met boerenkool, aardappelen en rookworst.',
    ingredients: [
      '1 kg kruimige aardappelen',
      '500 g verse boerenkool',
      '2 rookworsten',
      '200 ml warme melk',
      '50 g boter',
      '1 tl nootmuskaat',
      '2 uien, gesnipperd',
      'zout en peper naar smaak',
    ],
    instructions: [
      'Schil de aardappelen en snijd ze in gelijke stukken.',
      'Kook de aardappelen in gezouten water in 15 tot 20 minuten gaar.',
      'Was de boerenkool en verwijder de dikke nerven.',
      'Voeg de boerenkool toe aan de aardappelen in de laatste 5 minuten van de kooktijd.',
      'Verwarm de rookworsten volgens de aanwijzingen op de verpakking.',
      'Giet aardappelen en boerenkool af en stamp ze met boter en warme melk.',
      'Bak de uien glazig en roer ze door de stamppot.',
      'Breng op smaak met nootmuskaat, zout en peper en serveer met rookworst.',
    ],
    prep_time: 15,
    cook_time: 25,
    servings: 4,
    difficulty: 'gemakkelijk',
    meal_type: 'diner',
    tags: ['nederlands', 'stamppot', 'winter', 'traditioneel'],
    equipment: ['grote pan', 'stamper', 'koekenpan'],
    notes: 'Lekker met grove mosterd of een scheutje room.',
  },
  {
    ...baseRecipe,
    title: 'Klassieke Erwtensoep',
    description: 'Dikke Hollandse snert met rookworst, spek en wintergroenten.',
    ingredients: [
      '500 g spliterwten',
      '2 liter water',
      '1 rookworst',
      '200 g gerookt spek',
      '2 preien, in ringen',
      '2 wortels, in blokjes',
      '200 g knolselderij, in blokjes',
      '2 aardappelen, in blokjes',
      '2 laurierblaadjes',
      '1 stengel bleekselderij',
      'zout en peper naar smaak',
    ],
    instructions: [
      'Spoel de spliterwten af onder koud water.',
      'Breng het water aan de kook in een grote soeppan en voeg erwten, spek, rookworst en laurier toe.',
      'Laat de soep 1 uur zacht koken.',
      'Haal spek en rookworst uit de pan en snijd in stukken.',
      'Voeg prei, wortel, knolselderij, aardappel en bleekselderij toe.',
      'Kook nog 30 tot 45 minuten tot de groenten zacht zijn.',
      'Roer de soep goed zodat de erwten deels uit elkaar vallen.',
      'Voeg vlees terug, breng op smaak en serveer met roggebrood.',
    ],
    prep_time: 20,
    cook_time: 90,
    servings: 6,
    difficulty: 'middel',
    meal_type: 'diner',
    tags: ['nederlands', 'soep', 'winter', 'traditioneel'],
    equipment: ['soeppan', 'houten lepel'],
    notes: 'De soep smaakt de volgende dag nog beter.',
  },
  {
    ...baseRecipe,
    title: 'Nederlandse Pannenkoeken',
    description: 'Grote dunne pannenkoeken zoals je ze in een pannenkoekenhuis krijgt.',
    ingredients: [
      '300 g bloem',
      '3 eieren',
      '500 ml melk',
      '1 tl zout',
      '2 el suiker',
      'boter voor bakken',
      'stroop, suiker of poedersuiker voor serveren',
    ],
    instructions: [
      'Zeef de bloem in een kom.',
      'Voeg eieren en een scheut melk toe en roer glad.',
      'Voeg de rest van de melk, zout en suiker toe en meng tot een dun beslag.',
      'Laat het beslag 30 minuten rusten.',
      'Verhit een pannenkoekenpan met een beetje boter.',
      'Schenk een dunne laag beslag in de pan en bak tot de onderkant goudbruin is.',
      'Keer de pannenkoek om en bak de andere kant kort.',
      'Serveer warm met stroop, suiker of hartig beleg.',
    ],
    prep_time: 10,
    cook_time: 30,
    servings: 4,
    difficulty: 'gemakkelijk',
    meal_type: 'dessert',
    tags: ['nederlands', 'klassieker', 'weekend', 'familie'],
    equipment: ['koekenpan', 'garde', 'pollepel'],
    notes: 'Ook lekker met spek, appel of kaas.',
  },
  {
    ...baseRecipe,
    title: 'Hutspot met Klapstuk',
    description: 'Traditionele hutspot met ui, wortel, aardappel en mals draadjesvlees.',
    ingredients: [
      '1 kg kruimige aardappelen',
      '500 g winterwortels',
      '500 g uien',
      '800 g klapstuk',
      '100 ml warme melk',
      '50 g boter',
      '2 laurierblaadjes',
      '1 tl tijm',
      'zout en peper naar smaak',
    ],
    instructions: [
      'Kruid het vlees met zout en peper en braad het rondom aan.',
      'Voeg water toe tot het vlees net onder staat en laat met laurier en tijm 1,5 uur stoven.',
      'Schil aardappelen en wortels en snijd in stukken.',
      'Pel de uien en snijd in parten.',
      'Kook aardappelen, wortels en uien samen in 20 minuten gaar.',
      'Giet af en stamp met boter en warme melk.',
      'Snijd het gare vlees in plakken en serveer met de hutspot en wat jus.',
    ],
    prep_time: 20,
    cook_time: 120,
    servings: 6,
    difficulty: 'middel',
    meal_type: 'diner',
    tags: ['nederlands', 'stamppot', 'traditioneel', 'winter'],
    equipment: ['braadpan', 'grote pan', 'stamper'],
    notes: 'Ook lekker met een scheutje azijn door de hutspot.',
  },
  {
    ...baseRecipe,
    title: 'Zelfgemaakte Stroopwafels',
    description: 'Verse stroopwafels met warme kaneelstroop.',
    ingredients: [
      '250 g bloem',
      '125 g zachte boter',
      '100 g bruine suiker',
      '1 ei',
      '1 tl bakpoeder',
      '1 tl kaneel',
      'snufje zout',
      '2 el melk',
      '200 g bruine suiker voor de stroop',
      '100 g boter voor de stroop',
      '3 el stroop',
      '1 tl kaneel voor de stroop',
    ],
    instructions: [
      'Meng bloem, bakpoeder, kaneel en zout.',
      'Klop boter en suiker romig en voeg ei en melk toe.',
      'Meng alles tot een soepel deeg en laat 30 minuten rusten.',
      'Smelt voor de vulling suiker, boter, stroop en kaneel in een steelpan.',
      'Verwarm een stroopwafelijzer.',
      'Bak kleine deegbolletjes goudbruin in het ijzer.',
      'Snijd de wafels direct horizontaal open.',
      'Smeer de stroop ertussen en laat afkoelen op een rooster.',
    ],
    prep_time: 45,
    cook_time: 30,
    servings: 20,
    difficulty: 'moeilijk',
    meal_type: 'dessert',
    tags: ['nederlands', 'gebak', 'klassieker', 'zoet'],
    equipment: ['stroopwafelijzer', 'mixer', 'steelpan'],
    notes: 'Leg een warme stroopwafel even op je koffiekop voor extra zachte stroop.',
  },
  {
    ...baseRecipe,
    title: 'Zuurkoolstamppot met Spekjes',
    description: 'Winters stamppotgerecht met zuurkool, aardappel en krokante spekjes.',
    ingredients: [
      '1 kg kruimige aardappelen',
      '500 g zuurkool',
      '200 g spekreepjes',
      '200 ml melk',
      '50 g boter',
      '1 appel, in blokjes',
      '1 laurierblad',
      'zout en peper naar smaak',
    ],
    instructions: [
      'Kook de aardappelen in gezouten water gaar.',
      'Kook de zuurkool met laurierblad 15 minuten.',
      'Bak de spekreepjes krokant en voeg appelblokjes kort toe.',
      'Giet aardappelen en zuurkool af en verwijder het laurierblad.',
      'Stamp de aardappelen met boter en melk.',
      'Meng zuurkool en de helft van de spekjes erdoor.',
      'Breng op smaak en serveer met de rest van de spekjes bovenop.',
    ],
    prep_time: 15,
    cook_time: 30,
    servings: 4,
    difficulty: 'gemakkelijk',
    meal_type: 'diner',
    tags: ['nederlands', 'stamppot', 'winter', 'traditioneel'],
    equipment: ['grote pan', 'stamper', 'koekenpan'],
    notes: 'Lekker met rookworst of grove mosterd.',
  },
  {
    ...baseRecipe,
    title: 'Rauwe Andijviestamppot',
    description: 'Frisse andijviestamppot met spekjes en nootmuskaat.',
    ingredients: [
      '1 kg kruimige aardappelen',
      '400 g fijngesneden andijvie',
      '150 g spekblokjes',
      '200 ml warme melk',
      '40 g boter',
      '1 tl nootmuskaat',
      'zout en peper naar smaak',
    ],
    instructions: [
      'Kook de aardappelen in 20 minuten gaar.',
      'Bak de spekblokjes krokant.',
      'Stamp de aardappelen met boter, warme melk en nootmuskaat.',
      'Schep de rauwe andijvie door de hete puree.',
      'Roer de helft van de spekjes erdoor.',
      'Breng op smaak en serveer direct met de rest van de spekjes.',
    ],
    prep_time: 10,
    cook_time: 25,
    servings: 4,
    difficulty: 'gemakkelijk',
    meal_type: 'diner',
    tags: ['nederlands', 'stamppot', 'doordeweeks', 'traditioneel'],
    equipment: ['grote pan', 'stamper', 'koekenpan'],
    notes: 'Een scheutje azijn maakt de stamppot extra fris.',
  },
  {
    ...baseRecipe,
    title: 'Hachee met Rodekool',
    description: 'Klassieke stoofpot met rundvlees, ui, laurier en kruidnagel.',
    ingredients: [
      '800 g runderlappen',
      '600 g uien, in halve ringen',
      '50 g boter',
      '2 el bloem',
      '2 el azijn',
      '2 laurierblaadjes',
      '3 kruidnagels',
      '500 ml runderbouillon',
      'zout en peper naar smaak',
    ],
    instructions: [
      'Snijd het vlees in blokken en dep droog.',
      'Bak het vlees in boter rondom bruin.',
      'Haal het vlees uit de pan en bak de uien glazig.',
      'Doe het vlees terug en strooi de bloem erover.',
      'Voeg azijn, laurier, kruidnagel en bouillon toe.',
      'Laat het geheel 2,5 uur zacht stoven met deksel op de pan.',
      'Breng op smaak en serveer met rodekool of aardappelpuree.',
    ],
    prep_time: 20,
    cook_time: 165,
    servings: 4,
    difficulty: 'middel',
    meal_type: 'diner',
    tags: ['nederlands', 'stoofpot', 'winter', 'traditioneel'],
    equipment: ['braadpan', 'houten lepel'],
    notes: 'De smaak wordt nog beter als je hachee een dag eerder maakt.',
  },
  {
    ...baseRecipe,
    title: "Draadjesvlees op Oma's Manier",
    description: 'Langzaam gegaard rundvlees met een rijke jus.',
    ingredients: [
      '1 kg sukadelappen',
      '60 g boter',
      '2 uien, grof gesneden',
      '2 el appelstroop',
      '1 el mosterd',
      '2 laurierblaadjes',
      '400 ml runderbouillon',
      'zout en peper naar smaak',
    ],
    instructions: [
      'Bestrooi het vlees met zout en peper en bak het rondom bruin in boter.',
      'Voeg ui toe en bak 5 minuten mee.',
      'Roer mosterd en appelstroop erdoor.',
      'Voeg laurier en bouillon toe.',
      'Laat het vlees 3 uur heel zacht stoven met deksel op de pan.',
      'Keer af en toe om en controleer of er voldoende vocht in de pan zit.',
      'Laat de saus inkoken tot de gewenste dikte en serveer.',
    ],
    prep_time: 15,
    cook_time: 180,
    servings: 4,
    difficulty: 'middel',
    meal_type: 'diner',
    tags: ['nederlands', 'stoofpot', 'zondag', 'traditioneel'],
    equipment: ['braadpan'],
    notes: 'Gebruik vlees met wat vet voor extra smaak.',
  },
  {
    ...baseRecipe,
    title: 'Ambachtelijke Poffertjes',
    description: 'Luchtige mini-pannenkoekjes met boter en poedersuiker.',
    ingredients: [
      '250 g bloem',
      '250 ml melk',
      '1 ei',
      '7 g droge gist',
      '1 el suiker',
      'snufje zout',
      'boter voor bakken',
      'poedersuiker voor serveren',
    ],
    instructions: [
      'Meng bloem, gist, suiker en zout.',
      'Voeg melk en ei toe en klop tot een glad beslag.',
      'Laat het beslag 45 minuten rijzen.',
      'Verhit een ingevette poffertjespan.',
      'Vul de kuiltjes voor driekwart met beslag.',
      'Bak tot belletjes verschijnen, keer om en bak goudbruin.',
      'Serveer met boter en poedersuiker.',
    ],
    prep_time: 15,
    cook_time: 20,
    servings: 4,
    difficulty: 'middel',
    meal_type: 'dessert',
    tags: ['nederlands', 'dessert', 'snack', 'traditioneel'],
    equipment: ['poffertjespan', 'garde'],
    notes: 'Ook lekker met aardbeien of stroop.',
  },
  {
    ...baseRecipe,
    title: 'Klassieke Hollandse Appeltaart',
    description: 'Hoge appeltaart met kaneel, rozijnen en een krokant raster.',
    ingredients: [
      '300 g bloem',
      '200 g koude boter',
      '150 g suiker',
      '1 ei',
      '1 kg stevige appels',
      '75 g rozijnen',
      '2 tl kaneel',
      '2 el paneermeel',
    ],
    instructions: [
      'Kneed bloem, boter, suiker en ei tot een samenhangend deeg.',
      'Laat het deeg 30 minuten rusten in de koelkast.',
      'Verwarm de oven voor op 175 graden.',
      'Schil de appels en snijd ze in blokjes.',
      'Meng appel met rozijnen en kaneel.',
      'Bekleed een springvorm met twee derde van het deeg.',
      'Strooi paneermeel op de bodem en vul met het appelmengsel.',
      'Maak van de rest van het deeg repen en leg die als raster bovenop.',
      'Bak de taart 60 minuten goudbruin.',
    ],
    prep_time: 25,
    cook_time: 60,
    servings: 10,
    difficulty: 'middel',
    meal_type: 'dessert',
    tags: ['nederlands', 'gebak', 'dessert', 'traditioneel'],
    equipment: ['springvorm', 'oven', 'deegroller'],
    notes: 'Serveer met slagroom of vanille-ijs.',
  },
  {
    ...baseRecipe,
    title: 'Bitterballen uit de Frituur',
    description: 'Nederlandse borrelsnack met een smeuige ragoutvulling.',
    ingredients: [
      '300 g gaar rundvlees, fijn gesneden',
      '60 g boter',
      '60 g bloem',
      '500 ml runderbouillon',
      '1 ui, gesnipperd',
      '2 el peterselie, fijngehakt',
      '2 eieren',
      'paneermeel',
      'olie om te frituren',
      'zout en peper naar smaak',
    ],
    instructions: [
      'Fruit de ui in boter op laag vuur.',
      'Roer de bloem erdoor en gaar 2 minuten.',
      'Voeg geleidelijk de bouillon toe tot een dikke ragout ontstaat.',
      'Meng vlees en peterselie erdoor en laat de ragout opstijven.',
      'Vorm kleine balletjes van de koude massa.',
      'Haal ze door losgeklopt ei en daarna door paneermeel.',
      'Frituur 3 tot 4 minuten op 180 graden en serveer met mosterd.',
    ],
    prep_time: 35,
    cook_time: 25,
    servings: 8,
    difficulty: 'moeilijk',
    meal_type: 'snack',
    tags: ['nederlands', 'borrel', 'snack', 'traditioneel'],
    equipment: ['pan', 'frituurpan', 'zeef'],
    notes: 'Je kunt ongefrituurde bitterballen goed invriezen.',
  },
  {
    ...baseRecipe,
    title: 'Uitsmijter Ham en Kaas',
    description: 'Een klassieke Nederlandse lunch met brood, ham, kaas en gebakken eieren.',
    ingredients: [
      '4 sneetjes wit of bruin brood',
      '4 plakken ham',
      '4 plakken jong belegen kaas',
      '4 eieren',
      '20 g boter',
      'zout en peper naar smaak',
    ],
    instructions: [
      'Rooster of bak de boterhammen licht krokant.',
      'Beleg het brood met ham en kaas.',
      'Smelt boter in een koekenpan en bak de eieren.',
      'Bak tot het eiwit gestold is en de dooier nog zacht.',
      'Leg de eieren op de belegde boterhammen.',
      'Breng op smaak met zout en peper en serveer direct.',
    ],
    prep_time: 5,
    cook_time: 10,
    servings: 2,
    difficulty: 'gemakkelijk',
    meal_type: 'lunch',
    tags: ['nederlands', 'lunch', 'snel', 'klassieker'],
    equipment: ['koekenpan', 'spatel'],
    notes: 'Ook lekker met rosbief of spek.',
  },
  {
    ...baseRecipe,
    title: 'Kibbeling met Ravigottesaus',
    description: 'Krokante stukjes witvis in beslag, zoals van de viskraam.',
    ingredients: [
      '600 g kabeljauwfilet in stukken',
      '150 g bloem',
      '200 ml bruiswater',
      '1 tl paprikapoeder',
      '1 tl knoflookpoeder',
      'zout en peper naar smaak',
      'olie om te frituren',
      'ravigottesaus voor serveren',
    ],
    instructions: [
      'Dep de vis droog en bestrooi licht met zout.',
      'Meng bloem met paprikapoeder, knoflookpoeder, peper en zout.',
      'Voeg bruiswater toe en roer tot een glad beslag.',
      'Verhit frituurolie tot 180 graden.',
      'Haal de visstukken door het beslag.',
      'Frituur in porties 3 tot 4 minuten goudbruin.',
      'Laat uitlekken op keukenpapier en serveer met ravigottesaus.',
    ],
    prep_time: 15,
    cook_time: 15,
    servings: 4,
    difficulty: 'middel',
    meal_type: 'snack',
    tags: ['nederlands', 'vis', 'snack', 'streetfood'],
    equipment: ['frituurpan', 'mengkom', 'keukenpapier'],
    notes: 'Gebruik ijskoud bruiswater voor extra krokant beslag.',
  },
  {
    ...baseRecipe,
    title: 'Wentelteefjes met Kaneelsuiker',
    description: 'Ouderwets Nederlands recept om oud brood om te toveren tot een zoete traktatie.',
    ingredients: [
      '8 sneetjes oud witbrood',
      '2 eieren',
      '300 ml melk',
      '2 el suiker',
      '1 tl kaneel',
      '30 g boter',
      'extra kaneelsuiker voor serveren',
    ],
    instructions: [
      'Klop eieren, melk, suiker en kaneel los in een schaal.',
      'Week de sneetjes brood kort in het eimengsel.',
      'Verhit boter in een koekenpan op middelhoog vuur.',
      'Bak de sneetjes aan beide kanten goudbruin.',
      'Laat kort uitlekken op keukenpapier.',
      'Bestrooi royaal met kaneelsuiker en serveer warm.',
    ],
    prep_time: 10,
    cook_time: 10,
    servings: 4,
    difficulty: 'gemakkelijk',
    meal_type: 'dessert',
    tags: ['nederlands', 'ontbijt', 'dessert', 'traditioneel'],
    equipment: ['koekenpan', 'schaal', 'garde'],
    notes: 'Lekker met vers fruit of een lepel yoghurt.',
  },
]
